/**
 * API client for the embed widget.
 *
 * Every failure leaves here as an {@link EmbedApiError} carrying the status and
 * a Danish, showable message — the widget renders it verbatim, so the server's
 * own answer ("this domain is not on the token's allowlist") reaches the person
 * who can act on it instead of being flattened into "Failed to load app".
 */

import type { EmbedAppSchema } from '../types'
import type { FileJSON } from './file-value'

/**
 * The API's own origin. NOT the origin the widget script is served from: the
 * SPA host answers every `/api/*` path with the app shell at HTTP 200, so
 * defaulting to it turned a routing mistake into `SyntaxError: Unexpected token
 * '<'` from `res.json()`. Override with `apiBaseUrl` for self-hosted installs.
 */
const DEFAULT_API_BASE = 'https://api.agentbase.dk/api/v1'

export interface ApiOptions {
  baseUrl?: string
  token: string
}

/**
 * The backend marks an origin rejection with this header (app/api/cors.py) so
 * the widget can pick a heading without inferring it from status 403, which
 * the route also uses for "app not published".
 */
const EMBED_ERROR_HEADER = 'x-embed-error'
const ORIGIN_REJECTED = 'origin_rejected'

/** A failed API call, with the server's message when there was one. */
export class EmbedApiError extends Error {
  readonly status: number
  /** True when the origin is missing from the token's allowlist. */
  readonly isOriginRejected: boolean

  constructor(message: string, status: number, isOriginRejected = false) {
    super(message)
    this.name = 'EmbedApiError'
    this.status = status
    this.isOriginRejected = isOriginRejected
  }
}

/**
 * The origin to name in an error message. `apiBaseUrl` may be relative
 * (`/api/v1` on a same-origin self-host) — fetch accepts that, but
 * `new URL(url)` throws on it, and a throw inside an error path is how a
 * failed run used to become an unhandled rejection and a widget stuck on
 * "Kører…". Resolves against the page; falls back to the raw URL.
 */
export function describeOrigin(url: string): string {
  try {
    return new URL(url, typeof location !== 'undefined' ? location.href : undefined).origin
  } catch {
    return url
  }
}

/**
 * Turn a non-OK response into an error worth showing.
 *
 * A response that isn't JSON is the signature of `baseUrl` pointing at a web
 * server rather than the API, so it is named as such instead of surfacing as a
 * parse error three frames away.
 */
export async function toApiError(response: Response, url: string): Promise<EmbedApiError> {
  const body = await response.text().catch(() => '')
  const detail = extractDetail(body)

  if (detail === null) {
    return new EmbedApiError(
      `${describeOrigin(url)} svarede ikke med JSON på ${response.status}. ` +
        `Peger apiBaseUrl på AgentBase' API (fx ${DEFAULT_API_BASE})?`,
      response.status
    )
  }

  return new EmbedApiError(
    detail,
    response.status,
    response.headers.get(EMBED_ERROR_HEADER) === ORIGIN_REJECTED
  )
}

/**
 * The `detail` of a FastAPI error body, or null when the body is not JSON at
 * all — an HTML error page, a proxy notice, an SPA shell.
 */
function extractDetail(body: string): string | null {
  try {
    const parsed = JSON.parse(body) as unknown
    if (parsed && typeof parsed === 'object' && 'detail' in parsed) {
      const detail = (parsed as { detail: unknown }).detail
      if (typeof detail === 'string' && detail.trim()) return detail
    }
    return body.trim() || null
  } catch {
    return null
  }
}

/** Parse a successful response, naming an HTML body rather than throwing on `<`. */
async function readJson<T>(response: Response, url: string): Promise<T> {
  const body = await response.text()
  try {
    return JSON.parse(body) as T
  } catch {
    throw new EmbedApiError(
      `${describeOrigin(url)} svarede med noget, der ikke er JSON. ` +
        `Peger apiBaseUrl på AgentBase' API (fx ${DEFAULT_API_BASE})?`,
      response.status
    )
  }
}

/** A network-level failure — offline, DNS, or a CORS rejection. */
export function toNetworkError(url: string): EmbedApiError {
  return new EmbedApiError(
    `Der kunne ikke opnås forbindelse til ${describeOrigin(url)}. ` +
      `Kontrollér netværket, og at domænet står på embed-tokenens liste over tilladte domæner.`,
    0
  )
}

export class EmbedApiClient {
  private baseUrl: string
  private token: string

  constructor(options: ApiOptions) {
    // Trailing slashes would produce "//embed/…", which some proxies redirect
    // and others 404.
    this.baseUrl = (options.baseUrl || DEFAULT_API_BASE).replace(/\/+$/, '')
    this.token = options.token
  }

  /** Get the app schema (inputs/outputs) for rendering the widget. */
  async getSchema(appId: string): Promise<EmbedAppSchema> {
    const url = `${this.baseUrl}/embed/${appId}/schema?token=${encodeURIComponent(this.token)}`

    let response: Response
    try {
      response = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } })
    } catch {
      throw toNetworkError(url)
    }

    if (!response.ok) throw await toApiError(response, url)
    return readJson<EmbedAppSchema>(response, url)
  }

  /** Execute the app synchronously. */
  async execute(
    appId: string,
    inputs: Record<string, unknown>
  ): Promise<{ results: Record<string, unknown> }> {
    const url = `${this.baseUrl}/embed/${appId}/run?token=${encodeURIComponent(this.token)}`

    let response: Response
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ inputs }),
      })
    } catch {
      throw toNetworkError(url)
    }

    if (!response.ok) throw await toApiError(response, url)
    return readJson<{ results: Record<string, unknown> }>(response, url)
  }

  /**
   * Upload a picked file once and get back the reference the run names it by.
   * The bytes travel here, not inside the run request.
   */
  async uploadFile(appId: string, file: File): Promise<FileJSON> {
    const url = `${this.baseUrl}/embed/${appId}/files?token=${encodeURIComponent(this.token)}`
    const form = new FormData()
    form.append('file', file, file.name)

    let response: Response
    try {
      response = await fetch(url, { method: 'POST', headers: { Accept: 'application/json' }, body: form })
    } catch {
      throw toNetworkError(url)
    }

    if (!response.ok) throw await toApiError(response, url)
    const stored = await readJson<{
      file_id: string
      filename: string
      mime_type: string
      size_bytes: number
    }>(response, url)
    return {
      filename: file.name || stored.filename,
      mime_type: stored.mime_type,
      data: '',
      file_id: stored.file_id,
      size_bytes: stored.size_bytes,
    }
  }

  /** Get the streaming execution URL for SSE. */
  getStreamUrl(appId: string): string {
    return `${this.baseUrl}/embed/${appId}/run-stream?token=${encodeURIComponent(this.token)}`
  }
}
